/**
 * IOT-HAL-1: Map vendor-native event type strings to Kairos CanonicalEventType.
 *
 * Each adapter maintains its own vendor-specific event type map and delegates
 * to normalizeEventType. Unmapped vendor events return null — callers should
 * log and drop rather than forwarding raw vendor event types upstream.
 */

/**
 * @typedef {import('../contracts/DeviceContract.ts').CanonicalEventType} CanonicalEventType
 */

/**
 * All valid canonical event types. Source of truth is DeviceContract.ts;
 * this set is kept in sync for runtime validation.
 */
export const CANONICAL_EVENT_TYPES = new Set([
  'threshold_breach',
  'threshold_recovery',
  'shock_event',
  'door_open',
  'door_close',
  'geofence_enter',
  'geofence_exit',
  'low_battery',
  'connectivity_lost',
  'connectivity_restored',
  'firmware_updated',
  'tamper_detected',
  'self_test_failed',
  'config_pushed',
  'config_applied',
  'config_failed',
  'lifecycle_transition',
]);

/**
 * Normalize a vendor event type string to a canonical event type.
 *
 * @param {string} vendorEventType
 * @param {Record<string, CanonicalEventType>} vendorMap  adapter-specific lookup
 * @returns {CanonicalEventType | null}  null = unmapped vendor event; caller drops it
 */
export function normalizeEventType(vendorEventType, vendorMap) {
  if (!vendorEventType) return null;

  // Direct canonical pass-through (adapters that already speak canonical)
  if (CANONICAL_EVENT_TYPES.has(vendorEventType)) {
    return /** @type {CanonicalEventType} */ (vendorEventType);
  }

  const mapped = vendorMap[vendorEventType];
  if (!mapped) return null;

  if (!CANONICAL_EVENT_TYPES.has(mapped)) {
    throw new Error(
      `normalizeEventType: vendor map entry "${vendorEventType}" → "${mapped}" is not a canonical event type`
    );
  }

  return mapped;
}

/**
 * Validate that a string is a canonical event type at runtime.
 *
 * @param {string} eventType
 * @returns {boolean}
 */
export function isCanonicalEventType(eventType) {
  return CANONICAL_EVENT_TYPES.has(eventType);
}
